// Game-of-24 expression evaluator + validator.
//
// We deliberately do NOT reuse maffdoku's evaluate.ts (which pulls the `bigeval`
// github dependency and floats). Game of 24 needs two things bigeval doesn't give
// cleanly: (1) exact arithmetic so 8/(3-8/3) === 24 instead of 23.9999998, and
// (2) extraction of the integer operands actually used, so we can check the player
// used each of the four given numbers exactly once.
//
// Approach: a tiny recursive-descent parser over + - * / and parentheses, doing
// arithmetic in rationals (numerator/denominator pairs of JS numbers — safe for
// the small integers Game of 24 uses). Returns the value AND the multiset of
// integer literals encountered.

export type Rational = { n: number; d: number }

const gcd = (a: number, b: number): number => {
  a = Math.abs(a)
  b = Math.abs(b)
  while (b) {
    ;[a, b] = [b, a % b]
  }
  return a || 1
}

const makeRat = (n: number, d = 1): Rational => {
  if (d === 0) throw new Error('division by zero')
  if (d < 0) {
    n = -n
    d = -d
  }
  const g = gcd(n, d)
  return { n: n / g, d: d / g }
}

const add = (a: Rational, b: Rational) => makeRat(a.n * b.d + b.n * a.d, a.d * b.d)
const sub = (a: Rational, b: Rational) => makeRat(a.n * b.d - b.n * a.d, a.d * b.d)
const mul = (a: Rational, b: Rational) => makeRat(a.n * b.n, a.d * b.d)
const div = (a: Rational, b: Rational) => {
  if (b.n === 0) throw new Error('division by zero')
  return makeRat(a.n * b.d, a.d * b.n)
}

export type EvalResult = {
  ok: boolean // parsed and evaluated without error
  value: number | null // numeric value if ok
  rational: Rational | null
  operands: number[] // integer literals used, in order encountered
  error?: string
}

// Tokenise into numbers, operators, and parentheses. Only integers are allowed as
// literals (Game of 24 puzzles are integer-only); a decimal point is rejected so a
// player can't sneak in a 2.4 etc.
type Token = { type: 'num'; value: number } | { type: 'op'; value: string } | { type: 'lparen' } | { type: 'rparen' }

const tokenize = (input: string): Token[] => {
  const tokens: Token[] = []
  let i = 0
  // Normalise common spoken/typed multiplication & division glyphs.
  const s = input.replace(/[×✕✖]/g, '*').replace(/[÷]/g, '/').replace(/[−–—]/g, '-')
  while (i < s.length) {
    const c = s[i]
    if (c === ' ' || c === '\t') {
      i++
      continue
    }
    if (c >= '0' && c <= '9') {
      let num = ''
      while (i < s.length && s[i] >= '0' && s[i] <= '9') {
        num += s[i]
        i++
      }
      tokens.push({ type: 'num', value: parseInt(num, 10) })
      continue
    }
    if (c === '+' || c === '-' || c === '*' || c === '/') {
      tokens.push({ type: 'op', value: c })
      i++
      continue
    }
    if (c === '(' || c === '[' || c === '{') {
      tokens.push({ type: 'lparen' })
      i++
      continue
    }
    if (c === ')' || c === ']' || c === '}') {
      tokens.push({ type: 'rparen' })
      i++
      continue
    }
    if (c === '=') break // ignore a trailing "= 24" if the player typed one
    throw new Error(`unexpected character "${c}"`)
  }
  return tokens
}

// Recursive-descent: expr := term (('+'|'-') term)*; term := factor (('*'|'/') factor)*;
// factor := number | '(' expr ')' | ('-'|'+') factor.
const parseExpr = (tokens: Token[], operands: number[]) => {
  let pos = 0

  const peek = () => tokens[pos]
  const next = () => tokens[pos++]

  const factor = (): Rational => {
    const t = peek()
    if (!t) throw new Error('unexpected end of expression')
    if (t.type === 'op' && (t.value === '-' || t.value === '+')) {
      next()
      const f = factor()
      return t.value === '-' ? mul(makeRat(-1), f) : f
    }
    if (t.type === 'lparen') {
      next()
      const e = expr()
      if (!peek() || peek().type !== 'rparen') throw new Error('missing closing parenthesis')
      next()
      return e
    }
    if (t.type === 'num') {
      next()
      operands.push(t.value)
      return makeRat(t.value)
    }
    throw new Error('expected a number or "("')
  }

  const term = (): Rational => {
    let left = factor()
    while (peek() && peek().type === 'op' && (peek() as any).value !== '+' && (peek() as any).value !== '-') {
      const op = (next() as any).value
      const right = factor()
      left = op === '*' ? mul(left, right) : div(left, right)
    }
    return left
  }

  const expr = (): Rational => {
    let left = term()
    while (peek() && peek().type === 'op' && ((peek() as any).value === '+' || (peek() as any).value === '-')) {
      const op = (next() as any).value
      const right = term()
      left = op === '+' ? add(left, right) : sub(left, right)
    }
    return left
  }

  const value = expr()
  if (pos !== tokens.length) throw new Error('unexpected trailing input')
  return value
}

export const evaluateExpression = (input: string): EvalResult => {
  const operands: number[] = []
  try {
    const tokens = tokenize(input)
    if (tokens.length === 0) return { ok: false, value: null, rational: null, operands, error: 'empty expression' }
    const r = parseExpr(tokens, operands)
    return { ok: true, value: r.n / r.d, rational: r, operands }
  } catch (e: any) {
    return { ok: false, value: null, rational: null, operands, error: e?.message || 'parse error' }
  }
}

const sameMultiset = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false
  const sa = [...a].sort((x, y) => x - y)
  const sb = [...b].sort((x, y) => x - y)
  return sa.every((v, i) => v === sb[i])
}

export type Game24Verdict = {
  correct: boolean
  reason: 'correct' | 'wrong-value' | 'wrong-numbers' | 'parse-error' | 'empty'
  value: number | null
  usedNumbers: number[]
}

// The committed-answer judgement used on Commit. `correct` requires BOTH:
//   - the expression uses exactly the four given numbers, each once, and
//   - it evaluates to exactly 24.
// We still return a structured reason (never shown to the player during play —
// stored for offline analysis) so the corpus records *why* an answer was wrong.
export const judgeGame24 = (input: string, given: number[]): Game24Verdict => {
  const trimmed = (input || '').trim()
  if (!trimmed) return { correct: false, reason: 'empty', value: null, usedNumbers: [] }

  const res = evaluateExpression(trimmed)
  if (!res.ok) return { correct: false, reason: 'parse-error', value: null, usedNumbers: res.operands }

  const numbersOk = sameMultiset(res.operands, given)
  // Exact 24 via rationals: value is exact, so a plain compare is safe; keep a
  // tiny epsilon as belt-and-braces against any float drift.
  const valueOk = res.value !== null && Math.abs(res.value - 24) < 1e-9

  if (!numbersOk) return { correct: false, reason: 'wrong-numbers', value: res.value, usedNumbers: res.operands }
  if (!valueOk) return { correct: false, reason: 'wrong-value', value: res.value, usedNumbers: res.operands }
  return { correct: true, reason: 'correct', value: res.value, usedNumbers: res.operands }
}
